import {
  Container,
  Grid,
  Box,
  Button,
  useTheme,
  Slide,
  Fade,
  Grow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loading } from "../../components/shared/loading";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { HeroParticles } from "../../components/shared/HeroParticles";
import LanguageIcon from "@material-ui/icons/Language";
import AndroidIcon from "@material-ui/icons/Android";
import AppleIcon from "@material-ui/icons/Apple";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const useStyles = makeStyles((theme) => ({
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "45vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "40px 20px",
    maxWidth: 700,
  },
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  heroTitle: {
    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
    fontWeight: 700,
    marginBottom: 10,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroDivider: {
    width: 90,
    height: 3,
    background:
      "linear-gradient(90deg, transparent, #c77dc7 30%, #e0b8e0 50%, #c77dc7 70%, transparent)",
    margin: "16px auto",
    borderRadius: 2,
    animation: "$heroExpand 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
  },
  heroSubtitle: {
    fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
    fontWeight: 400,
    opacity: 0.9,
    maxWidth: 500,
    margin: "0 auto",
    lineHeight: 1.5,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both",
  },
  section: {
    padding: "40px 0",
    background: "#fafafa",
  },
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#4c004c",
    textAlign: "center",
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: "0.85rem",
    color: "#666666",
    textAlign: "center",
    maxWidth: 450,
    margin: "0 auto 28px",
    lineHeight: 1.5,
  },
  productCard: {
    background: "#ffffff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  },
  productHeader: {
    background: "linear-gradient(135deg, #6b1b6b 0%, #4c004c 100%)",
    padding: "32px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down('sm')]: {
      flexDirection: "column",
      textAlign: "center",
      gap: 16,
    },
  },
  productLogo: {
    width: 80,
    height: 80,
    objectFit: "contain",
    [theme.breakpoints.down('sm')]: {
      width: 60,
      height: 60,
    },
  },
  productTitle: {
    color: "#ffffff",
    fontSize: "1.3rem",
    fontWeight: 700,
    marginBottom: 4,
  },
  productTagline: {
    color: "rgba(255,255,255,0.85)",
    fontSize: "0.85rem",
  },
  productContent: {
    padding: "24px",
  },
  productDescription: {
    fontSize: "0.85rem",
    color: "#444444",
    lineHeight: 1.6,
    marginBottom: 20,
  },
  platformSection: {
    marginBottom: 24,
  },
  platformTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
    marginBottom: 12,
  },
  platformLinks: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  platformLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#f5f5f5",
    borderRadius: 8,
    textDecoration: "none",
    color: "#4c004c",
    fontSize: "0.8rem",
    fontWeight: 500,
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#4c004c",
      color: "#ffffff",
    },
  },
  servicesGrid: {
    marginTop: 20,
  },
  serviceItem: {
    background: "#fafafa",
    borderRadius: 10,
    padding: "16px",
    marginBottom: 12,
  },
  serviceTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
    marginBottom: 6,
  },
  serviceText: {
    fontSize: "0.8rem",
    color: "#666666",
    lineHeight: 1.5,
  },
  quoteSection: {
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: "12px",
    padding: "28px 24px",
    marginTop: 32,
    textAlign: "center",
  },
  quoteText: {
    fontSize: "0.9rem",
    color: "#ffffff",
    lineHeight: 1.6,
    fontStyle: "italic",
    maxWidth: 700,
    margin: "0 auto 12px",
  },
  quoteAuthor: {
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },
}));

export const Products = () => {
  const theme = useTheme();
  const classes = useStyles();

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [isloading, setIsloading] = useState(true);

  const handleClick = () => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  useEffect(() => {
    getMetaData();
  }, []);

  const getMetaData = () => {
    const formData = new FormData();
    formData.append("page", 3);
    Axios.post("/api/getmetadata", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        setMetaTitle(res.data.data.meta_title);
        setMetaKeywords(res.data.data.meta_keywords);
        setMetaDesc(res.data.data.meta_desc);
        setIsloading(false);
        handleClick();
      }
    });
  };

  const esayServices = [
    {
      title: "Contacts",
      description: "Access to a wide range of service providers, suppliers, engineers, and workforce contacts."
    },
    {
      title: "Engineering Info",
      description: "Comprehensive information for Consultants, Builders, Contractors, Engineers, and Students in construction."
    },
    {
      title: "Trends",
      description: "Latest practices and trends in the construction industry."
    },
    {
      title: "Conversions",
      description: "Unit converter covering length, area, weight, volume, stress, velocity, and more."
    },
    {
      title: "Careers",
      description: "Employment prospects for fresh graduates and experienced professionals in civil, mechanical, and electrical fields."
    },
  ];

  
  return (
    <div>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords}></meta>
      </Helmet>
      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <HeroParticles id="products-hero-particles" />
        <Fade in={true} timeout={1000}>
          <Box className={classes.heroContent}>
            <Typography className={classes.heroTitle}>
              Our Products
            </Typography>
            <Box className={classes.heroDivider} />
            <Typography className={classes.heroSubtitle}>
              Explore Our Innovative Solutions and Revolutionary IT Products
            </Typography>
          </Box>
        </Fade>
      </Box>

      {isloading ? (
        <Loading />
      ) : (
        <Box className={classes.section}>
          <Container maxWidth="lg">
            <Slide direction="up" in={true} timeout={800}>
              <Box>
                <Typography className={classes.sectionTitle}>
                  Our Flagship Product
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Innovative solutions designed for the construction industry
                </Typography>
              </Box>
            </Slide>

            {/* ESAY Product Card */}
            <Fade in={true} timeout={1000}>
              <Box className={classes.productCard}>
                {/* Product Header */}
                <Box className={classes.productHeader}>
                  <Box>
                    <Typography className={classes.productTitle}>
                      ESAY
                    </Typography>
                    <Typography className={classes.productTagline}>
                      Engineering Sources Around You
                    </Typography>
                  </Box>
                  <img
                    src="/static/products/Esay.png"
                    alt="ESAY Logo"
                    className={classes.productLogo}
                  />
                </Box>

                {/* Product Content */}
                <Box className={classes.productContent}>
                  <Typography className={classes.productDescription}>
                    ESAY offers an ideal platform for anyone interested in entering the construction industry. It serves as a hub where users can find contact details for service providers, suppliers, engineers, and labor forces, along with crucial information needed by professionals in the construction field.
                  </Typography>

                  {/* Platform Links */}
                  <Box className={classes.platformSection}>
                    <Typography className={classes.platformTitle}>
                      Available On
                    </Typography>
                    <Box className={classes.platformLinks}>
                      <a
                        href="https://esay.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.platformLink}
                      >
                        <LanguageIcon style={{ fontSize: 16 }} />
                        Web
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.esay.arawinzsoft"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.platformLink}
                      >
                        <AndroidIcon style={{ fontSize: 16 }} />
                        Android
                      </a>
                      <a
                        href="https://apps.apple.com/us/app/esay/id1490576735"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.platformLink}
                      >
                        <AppleIcon style={{ fontSize: 16 }} />
                        iOS
                      </a>
                    </Box>
                  </Box>

                  {/* Services */}
                  <Typography className={classes.platformTitle}>
                    Key Features
                  </Typography>
                  <Grid container spacing={2} className={classes.servicesGrid}>
                    {esayServices.map((service, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Grow in={true} timeout={800 + index * 100}>
                          <Box className={classes.serviceItem}>
                            <Typography className={classes.serviceTitle}>
                              {service.title}
                            </Typography>
                            <Typography className={classes.serviceText}>
                              {service.description}
                            </Typography>
                          </Box>
                        </Grow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Fade>

            {/* Quote Section */}
            <Fade in={true} timeout={1200}>
              <Box className={classes.quoteSection}>
                <TypingQuote
                  text={`"At Arawinz, our mission is to closely align with industry needs and develop products tailored to these requirements. ESAY is a solution specifically designed for the construction industry."`}
                  className={classes.quoteText}
                />
                <Typography className={classes.quoteAuthor}>
                  - Chethan. P. S. S, Managing Director
                </Typography>
              </Box>
            </Fade>
          </Container>
        </Box>
      )}
    </div>
  );
};

